import { AppStateKind, EVENT, parseStartParam, pathForDeepLink, sanitizeAppPath } from '@otryad/shared';
import { useEffect, useRef, useState } from 'react';
import { Navigate, useSearchParams } from 'react-router';

import { ErrorState, Screen, Spinner } from '@/components/ui';
import { useTrackOnce } from '@/lib/analytics';
import { draftStore, resumePath } from '@/lib/draft';
import { texts } from '@/lib/texts';
import { trpc } from '@/lib/trpc';

import { useTma } from './TmaProvider';

/** Маршрут по состоянию пользователя (BLUEPRINT 3.8). */
export const ROUTE_BY_STATE: Record<AppStateKind, string> = {
  [AppStateKind.needsOnboarding]: '/onboarding/city',
  [AppStateKind.inPool]: '/pool',
  [AppStateKind.inSquad]: '/home',
};

/**
 * Целевой экран: deep link `startapp=…` (кроме `ref_` — он ведёт на `/`) → `?path=` от кнопки бота →
 * маршрут по состоянию. Пользователь без онбординга идёт в онбординг (с того шага, где остановился);
 * исключение — приглашение запасного `reserve_<token>`: сначала экран приглашения, он предзаполнит
 * город и активность и отправит в онбординг сам (Stage 6, ADR-10).
 */
export function resolveTarget(
  state: AppStateKind,
  startParam: string | null,
  pathParam: string | null,
  draftStep: number,
): string {
  const link = parseStartParam(startParam);
  if (state === AppStateKind.needsOnboarding) {
    return link?.kind === 'reserve' ? pathForDeepLink(link) : resumePath(draftStep);
  }
  if (link && link.kind !== 'ref') return pathForDeepLink(link);
  return sanitizeAppPath(pathParam) ?? ROUTE_BY_STATE[state];
}

/** `/` → редирект по `auth.me`: onboarding | pool | squad, с учётом deep link. */
export function AppGate() {
  const me = trpc.auth.me.useQuery();
  const { startParam } = useTma();
  const [params] = useSearchParams();
  const attach = trpc.referral.attach.useMutation();
  const attached = useRef(false);
  const [draftReady, setDraftReady] = useState(false);

  // Первый шаг воронки (Stage 13, задача 1): сервер вход в Mini App не видит
  useTrackOnce(EVENT.appOpened);

  useEffect(() => {
    void draftStore.load().then(() => setDraftReady(true));
  }, []);

  // Реферальная ссылка без прохода через /start: привязка идёт в фоне, редирект не ждёт
  useEffect(() => {
    const link = parseStartParam(startParam);
    if (link?.kind === 'ref' && !attached.current) {
      attached.current = true;
      attach.mutate({ code: link.code });
    }
  }, [startParam, attach]);

  if (me.isPending || !draftReady) {
    return (
      <Screen>
        <div className="flex flex-1 flex-col items-center justify-center gap-3 py-24">
          <Spinner />
          <p className="text-muted text-sm">{texts.gate.checking}</p>
        </div>
      </Screen>
    );
  }

  if (me.isError) {
    return (
      <Screen>
        <ErrorState
          title={texts.gate.authFailedTitle}
          body={
            me.error.data?.code === 'UNAUTHORIZED'
              ? texts.gate.authFailedBody
              : texts.common.errorBody
          }
          onRetry={() => void me.refetch()}
        />
      </Screen>
    );
  }

  const target = resolveTarget(
    me.data.state,
    startParam,
    params.get('path'),
    draftStore.get().step,
  );
  return <Navigate to={target} replace />;
}
